/** src/lib/validations/submission.ts */
import { z } from 'zod'

export const submissionSchema = z.object({
  // Basic info
  enterprise_name: z.string().min(1, 'Enterprise name is required').max(255),
  candidate_name: z.string().min(1, 'Candidate name is required').max(255),
  father_or_husband_name: z.string().max(255).optional(),
  gender: z.enum(['Male', 'Female', 'Other']).optional(),
  email: z.string().email('Invalid email address').max(255).optional().or(z.literal('')),
  phone: z.string().max(50).optional(),
  aadhaar_number: z
    .string()
    .regex(/^\d{12}$/, 'Aadhaar must be 12 digits')
    .optional()
    .or(z.literal('')),
  date_of_birth: z.string().max(50).optional(),
  age: z.string().max(10).optional(),
  name_of_scheme: z.string().max(255).optional(),

  // Residential address
  residential_address_vill_moh: z.string().max(255).optional(),
  residential_address_po: z.string().max(255).optional(),
  residential_address_ps: z.string().max(255).optional(),
  residential_address_block: z.string().max(255).optional(),
  residential_address_district: z.string().max(255).optional(),
  residential_address_pincode: z
    .string()
    .regex(/^\d{6}$/, 'Pincode must be 6 digits')
    .optional()
    .or(z.literal('')),

  // Unit location
  location_of_unit_vill_moh: z.string().max(255).optional(),
  location_of_unit_po: z.string().max(255).optional(),
  location_of_unit_ps: z.string().max(255).optional(),
  location_of_unit_block: z.string().max(255).optional(),
  location_of_unit_district: z.string().max(255).optional(),
  location_of_unit_pincode: z
    .string()
    .regex(/^\d{6}$/, 'Pincode must be 6 digits')
    .optional()
    .or(z.literal('')),
  location_type: z.enum(['Urban', 'Rural']).optional(),

  // Enterprise details
  category_of_enterprise: z.enum(['Manufacturing', 'Service Sector']).optional(),
  social_category: z.enum(['SC', 'ST', 'OBC', 'Female', 'Handicapped', 'General']).optional(),
  date_of_birth_or_age: z.string().max(50).optional(),
  educational_qualification: z.enum(['8th', '10th', '12th', 'Graduation', 'Post Graduation', 'Other']).optional(),
  type_of_organisation: z.enum(['Proprietorship', 'Partnership', 'Private Limited Company', 'Other']).optional(),
  product_name: z.string().max(255).optional(),
  ifsc_code: z.string().max(11).optional(),
  bank_name: z.string().max(255).optional(),
  branch_name: z.string().max(255).optional(),
  branch_address: z.string().max(500).optional(),

  // Ownership
  land_ownership: z.enum(['Own', 'Leased', 'Rent']).optional(),
  lease_or_rent_amount: z.string().max(50).optional(),
  building_ownership: z.enum(['Own', 'Rent']).optional(),
  building_amount: z.string().max(50).optional(),
  machine_value: z.string().max(50).optional(),
  other_fixed_assets: z.string().max(255).optional(),

  // Financial
  total_term_loan_required: z.string().max(50).optional(),
  working_capital_loan_required: z.string().max(50).optional(),
  total_project_cost: z.string().max(50).optional(),
  service_area_bank_name: z.string().max(255).optional(),

  // ✅ Added new branch fields
  service_area_bank_branch_name: z.string().max(255).optional(),
  service_area_bank_branch_address: z.string().max(255).optional(),
  service_area_bank_branch_city: z.string().max(255).optional(),
  service_area_bank_branch_state: z.string().max(255).optional(),
  service_area_bank_branch_pincode: z
    .string()
    .regex(/^\d{6}$/, 'Pincode must be 6 digits')
    .optional()
    .or(z.literal('')),
  service_area_bank_branch_ifsc_code: z
    .string()
    .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'Invalid IFSC code format')
    .optional()
    .or(z.literal('')),

  contribution_option: z.string().max(255).optional(),

  // Additional
  notes: z.string().max(5000).optional(),

  // Honeypot field (should be empty)
  website: z
    .string()
    .max(0, 'Invalid input')
    .optional()
    .or(z.literal('')),
})

export type SubmissionFormData = z.infer<typeof submissionSchema>
